import { useState } from "react"
import { Alert, Button, Checkbox, Form, Input, Select } from "antd"
import { Link, useNavigate } from "react-router-dom"
import { LockOutlined, MailOutlined } from "@ant-design/icons"
import AuthShell from "../../Layout/AuthShell"
import apiClient from "../../Utils/apiClient"
import generateStrongPassword from "../../Utils/generateStrongPassword"
import { productName } from "../../config"
import { rememberTenant } from "../../Utils/tenantStorage"

const suggestSlug = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 63)
    .replace(/-+$/g, "")

const sizes = ["1–50", "51–200", "201–500", "501–2,000", "2,001+"]
const audiences = [
  "IT department",
  "Managed service provider",
  "IT consultant",
  "Other",
]

const SignupTrial = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const [done, setDone] = useState(null)
  const [slugTouched, setSlugTouched] = useState(false)

  const onCompany = (event) => {
    if (slugTouched) return
    form.setFieldValue("slug", suggestSlug(event.target.value))
  }

  const onFinish = async (values) => {
    setSaving(true)
    setError("")
    try {
      const res = await apiClient(
        "platform/signup",
        "POST",
        {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: values.password,
          passwordConfirm: values.passwordConfirm,
          companyName: values.companyName,
          slug: values.slug,
          companySize: values.companySize,
          phone: values.phone,
          jobTitle: values.jobTitle,
          audience: values.audience,
          acceptTerms: true,
        },
        { refresh: false, headers: { "X-Tenant": "" } }
      )
      if (!res?.success) {
        setError(res?.message || "Could not start the trial.")
        return
      }
      rememberTenant(res.data.slug, { home: true })
      setDone(res.data)
    } catch (err) {
      setError(err?.message || "Could not start the trial.")
    } finally {
      setSaving(false)
    }
  }

  if (done) {
    const ends = done.trialEndsAt
      ? new Date(done.trialEndsAt).toLocaleDateString(undefined, {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "30 days from today"
    return (
      <AuthShell
        title="Your trial is ready"
        subtitle={`${done.organization} can use ${productName} until ${ends}.`}
        badge="30-day trial"
        steps={["Company", "Account", "Console"]}
        activeStep={3}
      >
        <Alert
          type="success"
          showIcon
          className="mb-3"
          message="No payment is due today"
          description={`Sign in with organization ${done.slug} and ${done.email}. The first sign-in asks you to set up two-factor authentication.`}
        />
        <Button type="primary" block size="large" onClick={() => navigate("/login", { replace: true })}>
          Sign in
        </Button>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      wide
      title="Start a 30-day trial"
      subtitle={`Create your company on ${productName}. No credit card required.`}
      badge="Free trial"
      steps={["Company", "Account", "Console"]}
      activeStep={1}
      footer={<Link to="/login">Already have an account? Sign in</Link>}
    >
      {error ? (
        <Alert type="error" showIcon className="mb-3" message={error} closable onClose={() => setError("")} />
      ) : null}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
        <div className="auth-split">
          <Form.Item label="First name" name="firstName" rules={[{ required: true, message: "Enter your first name" }]}>
            <Input size="large" autoComplete="given-name" />
          </Form.Item>
          <Form.Item label="Last name" name="lastName" rules={[{ required: true, message: "Enter your last name" }]}>
            <Input size="large" autoComplete="family-name" />
          </Form.Item>
        </div>
        <Form.Item
          label="Work email"
          name="email"
          rules={[
            { required: true, message: "Enter your work email" },
            { type: "email", message: "Enter a valid email" },
          ]}
        >
          <Input size="large" prefix={<MailOutlined />} autoComplete="email" placeholder="you@company.com" />
        </Form.Item>
        <Form.Item label="Company name" name="companyName" rules={[{ required: true, message: "Enter the company name" }]}>
          <Input size="large" autoComplete="organization" onChange={onCompany} />
        </Form.Item>
        <Form.Item
          label="Organization slug"
          name="slug"
          extra="People sign in with this name. For example, northwind."
          rules={[
            { required: true, message: "Choose an organization slug" },
            {
              pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
              message: "Use lowercase letters, numbers, and hyphens.",
            },
          ]}
        >
          <Input size="large" autoComplete="off" onChange={() => setSlugTouched(true)} />
        </Form.Item>
        <div className="auth-split">
          <Form.Item label="Company size" name="companySize">
            <Select size="large" allowClear placeholder="Employees" options={sizes.map((value) => ({ value, label: value }))} />
          </Form.Item>
          <Form.Item label="You are" name="audience">
            <Select size="large" allowClear placeholder="Choose one" options={audiences.map((value) => ({ value, label: value }))} />
          </Form.Item>
        </div>
        <div className="auth-split">
          <Form.Item label="Job title" name="jobTitle">
            <Input size="large" autoComplete="organization-title" />
          </Form.Item>
          <Form.Item label="Phone" name="phone">
            <Input size="large" autoComplete="tel" />
          </Form.Item>
        </div>
        <Form.Item
          label="Password"
          name="password"
          rules={[
            { required: true, message: "Choose a password" },
            { min: 8, message: "Use at least 8 characters" },
          ]}
        >
          <Input.Password size="large" prefix={<LockOutlined />} autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          label="Confirm password"
          name="passwordConfirm"
          dependencies={["password"]}
          rules={[
            { required: true, message: "Confirm your password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) return Promise.resolve()
                return Promise.reject(new Error("Passwords do not match"))
              },
            }),
          ]}
        >
          <Input.Password size="large" prefix={<LockOutlined />} autoComplete="new-password" />
        </Form.Item>
        <Button
          className="mb-3"
          onClick={() => {
            const password = generateStrongPassword(12)
            form.setFieldsValue({ password, passwordConfirm: password })
          }}
        >
          Generate a password
        </Button>
        <Form.Item
          name="acceptTerms"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(new Error("Accept the privacy policy to continue.")),
            },
          ]}
        >
          <Checkbox>
            I agree to the <Link to="/privacy">privacy policy</Link>. No credit card is required.
          </Checkbox>
        </Form.Item>
        <Form.Item className="mb-0">
          <Button type="primary" htmlType="submit" block size="large" loading={saving}>
            Start free trial
          </Button>
        </Form.Item>
      </Form>
    </AuthShell>
  )
}

export default SignupTrial
